var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useCallback, useState } from 'react';
import { useImmer } from 'use-immer';
import { updateLevel } from '../../api/levels';
import ImageDropzone from '../ImageDropzone/ImageDropzone';
import { MAP_BACKGROUND_SIZE } from '../../utils/mapGeometry';
import { FaLock } from 'react-icons/fa';
// import SliderInput from '../SliderInput/SliderInput'; // re-add with the encounter-probability slider
import './LevelPropertiesEditor.scss';
import { levelEditLock, MAP_GRID_CELLS } from '@common/contracts';
export function LevelPropertiesEditor({ level: initialLevelData, isOpen, onClose }) {
    const [level, updateLevelState] = useImmer(initialLevelData);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    // Moderation edit-lock (same rule everywhere): Pending = nothing editable,
    // Approved = only the background map image, Draft/Rejected = fully editable.
    const editLock = levelEditLock(level.moderationStatus);
    const locked = editLock === 'locked'; // Pending — frozen
    const imageOnly = editLock === 'image-only'; // Approved — only background image
    const fieldsDisabled = isSaving || locked || imageOnly;
    // The shared demo level (City-18, defaultKey set) is fully READ-ONLY: you can
    // open its settings to look, but nothing can be changed — including the name.
    const isDemo = level.defaultKey != null;
    // A level created from an admin challenge is bound to that brief: every
    // property is fixed by the challenge except the level name. (challengeId is
    // set at participate time.) Only the name stays editable here.
    const isChallengeEntry = level.challengeId != null;
    // The name is editable normally, but frozen for a demo (and while locked/approved).
    const nameDisabled = fieldsDisabled || isDemo;
    const nonNameDisabled = fieldsDisabled || isChallengeEntry || isDemo;
    // Reset state when the initial level data changes (e.g., opening editor for a different level)
    useEffect(() => {
        updateLevelState(initialLevelData);
        setError(null); // Clear previous errors
        setIsSaving(false);
    }, [initialLevelData, updateLevelState]);
    // Generic handler to update state
    const handleValueChange = (name, newValue) => {
        updateLevelState(draft => {
            if (draft) {
                draft[name] = newValue;
            }
        });
    };
    // Specific handler for simple text inputs (like name)
    const handleTextInputChange = (e) => {
        const { name, value } = e.target;
        handleValueChange(name, value);
    };
    // Specific handler for INTEGER inputs (mapWidth, mapHeight, moveCost, minimumPower)
    const handleIntegerInputChange = (e) => {
        const { name, value } = e.target;
        const digits = value.replace(/\D/g, ''); // Remove non-digits
        if (digits === '') {
            // If the input is cleared (empty string after removing non-digits), set state to null
            handleValueChange(name, null);
        }
        else {
            const parsedValue = parseInt(digits, 10);
            // Only update if parsing was successful (parseInt returns NaN for things like empty string, though we handle empty above)
            if (!isNaN(parsedValue)) {
                handleValueChange(name, parsedValue);
            }
            else {
                // Fallback if somehow parsing digits resulted in NaN - set to null
                handleValueChange(name, null);
            }
        }
    };
    // The map size is fixed (the game runs MAP_GRID_CELLS boards only), so the field
    // is read-only and there is no change handler to write it.
    // Specific handler for SliderInput (encounter probability — hidden for now).
    // const handleSliderChange = (newValue: number) => {
    //     // Slider returns 0-100, we need 0-1 for encounterProbability
    //     handleValueChange('encounterProbability', newValue / 100);
    // };
    // Callback for successful image upload
    const handleImageUpload = useCallback((filePath) => {
        handleValueChange('backgroundImage', filePath);
    }, [updateLevelState]); // Depend on updateLevelState
    const handleSave = () => __awaiter(this, void 0, void 0, function* () {
        if (!level)
            return;
        if (locked)
            return; // Pending — nothing can be saved
        if (isDemo) {
            onClose(false);
            return;
        } // Demo level — read-only, nothing to save
        setIsSaving(true);
        setError(null);
        // Approved levels are frozen except the background map image — send ONLY that
        // field (a full PATCH would be rejected by the backend edit-lock with 423).
        // Checked BEFORE the challenge branch so an approved challenge entry (a
        // challenge "winner") re-saves its unchanged, locked background as a harmless
        // no-op instead of PATCHing { name } and hitting the 423 edit-lock.
        if (imageOnly) {
            try {
                yield updateLevel(level.id, { backgroundImage: level.backgroundImage });
                onClose(true);
            }
            catch (err) {
                console.error('Error saving level background:', err);
                setError(`Failed to save background image: ${err.message}`);
                setIsSaving(false);
            }
            return;
        }
        // Challenge entries (in an editable moderation state) are bound to the admin's
        // brief: only the name is editable, so send just the name (every other field
        // is fixed).
        if (isChallengeEntry) {
            try {
                yield updateLevel(level.id, { name: level.name });
                onClose(true);
            }
            catch (err) {
                console.error('Error saving level name:', err);
                setError(`Failed to save name: ${err.message}`);
                setIsSaving(false);
            }
            return;
        }
        // Final validation and defaulting before saving
        const finalDraft = Object.assign({}, level); // Clone current state for validation
        // Validate and default the (square) map size — width and height stay equal.
        let size = finalDraft.mapWidth;
        if (typeof size !== 'number' || size < 1) {
            size = 1;
        }
        finalDraft.mapWidth = size;
        finalDraft.mapHeight = size;
        // Validate and default moveCost
        let cost = finalDraft.moveCost;
        if (typeof cost !== 'number' || cost < 1) { // Assuming 1 is the minimum allowed cost
            cost = 1;
        }
        finalDraft.moveCost = cost;
        try {
            // Use the validated finalDraft for the update
            yield updateLevel(level.id, finalDraft);
            onClose(true); // Close modal and indicate refresh needed
        }
        catch (err) { // Catch specific error type if possible
            console.error('Error saving level properties:', err);
            setError(`Failed to save properties: ${err.message}`);
            setIsSaving(false);
        }
    });
    const handleCancel = () => {
        onClose(false); // Close modal, no refresh needed
    };
    // Prevent rendering if not open
    if (!isOpen) {
        return null;
    }
    // Helper to display number values (handles 0 correctly)
    const getInputValue = (value) => {
        // If it's 0, display '0', otherwise convert to string or empty string
        if (value === 0)
            return '0';
        if (value === null || value === undefined || value === '')
            return '';
        return String(value);
    };
    // Avatar Restriction / Rarity controls are hidden — levels no longer expose
    // them (values stay at their defaults). Re-add these handlers with that row.
    // const handleToggleAvatarMode = () => {
    //     const currentMode = level.allowedAvatarMode;
    //     const nextMode = currentMode === AvatarMode.Minimum ? AvatarMode.Exclusive : AvatarMode.Minimum;
    //     handleValueChange('allowedAvatarMode', nextMode);
    // };
    // const handleCycleAvatarRarity = () => {
    //     const currentRarity = level.allowedAvatarRarity;
    //     const currentIndex = avatarRarities.indexOf(currentRarity);
    //     const nextIndex = (currentIndex + 1) % avatarRarities.length; // Cycle through the array
    //     const nextRarity = avatarRarities[nextIndex];
    //     handleValueChange('allowedAvatarRarity', nextRarity);
    // };
    return (_jsx("div", { className: "modal-overlay", children: _jsxs("div", { className: "modal-content level-properties-editor", children: [_jsxs("span", { className: "modal-level-id", children: ["ID: ", level.id] }), _jsx("h2", { children: level.name }), (locked || imageOnly || isChallengeEntry || isDemo) && (_jsxs("div", { className: "lpe-lock-note", children: [_jsx(FaLock, { style: { marginRight: 6, verticalAlign: '-1px' } }), " ", isDemo
                            ? 'This is a shared demo level — its settings are read-only. Build your own Simulation to configure it.'
                            : locked
                                ? 'This level is under review — all properties are locked.'
                                : (imageOnly && isChallengeEntry)
                                    ? 'This level is approved and was created from a challenge — all properties are locked.'
                                    : isChallengeEntry
                                        ? 'This level was created from a challenge — only its name can be changed. Every other setting is fixed by the challenge.'
                                        : 'This level is approved — you can only change the background map image.'] })), error && _jsx("div", { className: "error-message", children: error }), _jsxs("form", { onSubmit: (e) => { e.preventDefault(); handleSave(); }, children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "name", children: "Level Name:" }), _jsx("input", { type: "text", id: "name", name: "name", value: level.name, onChange: handleTextInputChange, disabled: nameDisabled, required: true })] }), _jsxs("div", { className: "form-row row-3-inputs", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "mapSize", children: "Map Size (square):" }), _jsx("input", { type: "text", id: "mapSize", name: "mapSize", value: getInputValue(level.mapWidth), pattern: "\\d*", inputMode: "numeric", required: true, disabled: true, readOnly: true, title: `Simulations are always ${MAP_GRID_CELLS}×${MAP_GRID_CELLS}` })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "moveCost", children: "Move Cost (Power):" }), _jsx("input", { type: "text", id: "moveCost", name: "moveCost", value: getInputValue(level.moveCost), onChange: handleIntegerInputChange, pattern: "\\d*", inputMode: "numeric", required: true, disabled: nonNameDisabled })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "minimumPower", children: "Minimum Power" }), _jsx("input", { type: "number", id: "minimumPower", name: "minimumPower", value: getInputValue(level.minimumPower), onChange: handleIntegerInputChange, min: "0", pattern: "\\d*", inputMode: "numeric", disabled: nonNameDisabled })] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { id: "map-image-label", children: "World map (square):" }), " ", _jsx(ImageDropzone, { requireSquare: true, recommendedSize: MAP_BACKGROUND_SIZE, kind: "world-map", currentImageUrl: level.backgroundImage, onUploadSuccess: handleImageUpload, disabled: isSaving || locked || isChallengeEntry || isDemo, "aria-labelledby": "map-image-label" // Associate with label
                                 })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: handleCancel, disabled: isSaving, children: isDemo ? 'Close' : 'Cancel' }), !isDemo && (_jsx("button", { type: "submit", disabled: isSaving || locked, children: isSaving ? 'Saving...' : 'Save Changes' }))] })] })] }) }));
}
